import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import {
    MdSearch,
    MdVisibility,
    MdFilterList,
    MdClose,
    MdEventNote
} from 'react-icons/md';
import { formatDate } from '../utils/dateUtils';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import Pagination from '../components/common/Pagination';
import Drawer from '../components/common/Drawer';
import { authFetch } from '../components/utils/authFetch';
import { toast } from 'react-toastify';
import { GET_HISTORY_LOGS_API, GET_ALL_EMPLOYEES_API } from '../config/API';

import styles from './HistoryLog.module.css';

const HistoryLog = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters
    const [showFilters, setShowFilters] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [employeeName, setEmployeeName] = useState('');
    const [eventType, setEventType] = useState('');
    const [employees, setEmployees] = useState([]);

    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Log Details Drawer
    const [selectedLog, setSelectedLog] = useState(null);
    const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);

    const employeeOptions = useMemo(() => {
        return employees.map(emp => ({
            value: `${emp.firstName} ${emp.lastName}`,
            label: `${emp.firstName} ${emp.lastName}`
        }));
    }, [employees]);

    const eventTypeOptions = [
        { value: 'login', label: 'Login' },
        { value: 'create', label: 'Create' },
        { value: 'update', label: 'Update' },
        { value: 'delete', label: 'Delete' },
        { value: 'send_po_pdf_whatsapp', label: 'Send PO PDF WhatsApp' },
        { value: 'po_cancel', label: 'PO Cancel' },
        { value: 'send_for_compare_po', label: 'Send For Compare PO' },
        { value: 'send_message', label: 'Send Message' },
        { value: 'change_password', label: 'Change Password' },
    ];

    // Custom Styles for React Select
    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#4f46e5' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(79, 70, 229, 0.1)' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#4f46e5' : '#9ca3af',
            },
            borderRadius: '6px',
            fontSize: '14px',
            minHeight: '38px',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#eef2ff' : 'white',
            color: state.isSelected ? 'white' : '#374151',
            cursor: 'pointer',
            fontSize: '14px',
            '&:active': {
                backgroundColor: '#4f46e5',
            },
        }),
        placeholder: (base) => ({
            ...base,
            color: '#9ca3af',
        }),
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(employeeName);
        }, 500);
        return () => clearTimeout(timer);
    }, [employeeName]);

    const fetchEmployees = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await authFetch(`${GET_ALL_EMPLOYEES_API}?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.status) {
                setEmployees(result.data || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=10`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (debouncedSearch) queryParams += `&employeeName=${debouncedSearch}`;
            if (eventType) queryParams += `&event_type=${eventType}`;

            const response = await authFetch(`${GET_HISTORY_LOGS_API}${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const result = await response.json();

            if (result.status) {
                setLogs(result.data || []);
                setTotalPages(result.totalPages || 1);
                setTotalRecords(result.totalRecords || 0);
            } else {
                setLogs([]);
                setTotalPages(1);
                setTotalRecords(0);
                if (result.message && result.message !== 'No history logs found') {
                    toast.error(result.message);
                }
            }
        } catch (error) {
            console.error('Error fetching history logs:', error);
            toast.error('Failed to fetch history logs');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [currentPage, debouncedSearch, dateRange, eventType]);

    const handleViewDetails = (log) => {
        setSelectedLog(log);
        setShowDetailsDrawer(true);
    };

    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return <span className={styles['null-value']}>—</span>;
        }

        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }

        if (Array.isArray(value)) {
            if (value.length === 0) return <span className={styles['null-value']}>Empty</span>;
            return (
                <div className={styles['nested-array']}>
                    {value.map((item, index) => (
                        <div key={index} className={styles['nested-array-item']}>
                            {formatValue(item)}
                        </div>
                    ))}
                </div>
            );
        }

        if (typeof value === 'object' && value !== null) {
            const EXCLUDED_FIELDS = ['id', 'uniqno', '1', '_changed', '_options', 'isnewrecord'];
            const filteredEntries = Object.entries(value).filter(([key, val]) => {
                const lowerKey = key.toLowerCase();
                // Skip excluded fields, ID fields, and empty values
                return !EXCLUDED_FIELDS.includes(lowerKey) &&
                    !lowerKey.endsWith('id') &&
                    val !== null && val !== undefined && val !== '';
            });

            if (filteredEntries.length === 0) return <span className={styles['null-value']}>—</span>;

            return (
                <div className={styles['nested-object']}>
                    {filteredEntries.map(([key, val]) => (
                        <div key={key} className={styles['nested-row']}>
                            <span className={styles['nested-label']}>{key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:</span>
                            <span className={styles['nested-value']}>{formatValue(val)}</span>
                        </div>
                    ))}
                </div>
            );
        }

        return String(value);
    };

    const getReadableDetails = (details) => {
        if (!details) return 'No additional details available';
        try {
            const obj = typeof details === 'string' ? JSON.parse(details) : details;

            // If it's the specific format with action and data
            if (obj && obj.action) return obj.action;

            // If no action key but has other data, format it nicely
            const keys = Object.keys(obj);
            if (keys.length > 0) {
                return keys.map(key => {
                    const value = obj[key];
                    const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
                    return `${label}: ${value === null ? 'N/A' : (typeof value === 'object' ? JSON.stringify(value) : value)}`;
                }).join(', ');
            }
            return 'Activity recorded';
        } catch (e) {
            return details.toString();
        }
    };

    const getEventBadgeClass = (type) => {
        if (!type) return styles['event-default'];
        const t = type.toLowerCase();
        if (t.includes('create')) return styles['event-create'];
        if (t.includes('update')) return styles['event-update'];
        if (t.includes('delete') || t.includes('cancel')) return styles['event-delete'];
        if (t.includes('login') || t.includes('password')) return styles['event-login'];
        if (t.includes('message') || t.includes('send') || t.includes('whatsapp')) return styles['event-info'];
        return styles['event-default'];
    };

    return (
        <div className={styles['history-container']}>
            <div className={styles['history-header']}>
                <h2>History Log</h2>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter History Logs"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Event Type</label>
                        <Select
                            options={eventTypeOptions}
                            value={eventTypeOptions.find(opt => opt.value === eventType) || null}
                            onChange={(selected) => setEventType(selected ? selected.value : '')}
                            placeholder="Select Event Type..."
                            isClearable
                            isSearchable={false}
                            styles={customSelectStyles}
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Associate Name</label>
                        <Select
                            options={employeeOptions}
                            value={employeeOptions.find(opt => opt.value === employeeName) || null}
                            onChange={(selected) => setEmployeeName(selected ? selected.value : '')}
                            placeholder="Select Associate Name..."
                            isClearable
                            isSearchable
                            styles={customSelectStyles}
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>End Date</label>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                setEmployeeName('');
                                setEventType('');
                                setDebouncedSearch('');
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset All Filters
                        </button>
                    </div>
                </div>
            </Drawer>

            <div className={styles['card-container-history']}>
                <div className={styles['search-container']}>
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search..."
                            className={styles['search-input']}
                            value={employeeName}
                            onChange={(e) => setEmployeeName(e.target.value)}
                        />
                        {employeeName && (
                            <button
                                className={styles['clear-search-btn']}
                                onClick={() => setEmployeeName('')}
                                title="Clear search"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles['table-container']}>
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Sn</th>
                                <th style={{ width: '180px' }}>Date & Time</th>
                                <th style={{ width: '150px' }}>Associate Name</th>
                                <th>Action Performed</th>
                                <th style={{ width: '130px', textAlign: 'center' }}>Event Name</th>
                                <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <Loader text="Fetching activity logs..." />
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <NoData title="No History Logs Found" message="Try adjusting your search or filters." />
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log, index) => (
                                    <tr key={log.id}>
                                        <td>{(currentPage - 1) * 10 + index + 1}</td>
                                        <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>{formatDate(log.date)} {new Date(log.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                        <td>
                                            <strong style={{ fontSize: '13px' }}>{log.Employee?.firstName} {log.Employee?.lastName}</strong>
                                        </td>
                                        <td style={{ fontSize: '0.85rem', color: '#4b5563' }}>
                                            {getReadableDetails(log.details)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={`${styles['event-type-badge']} ${getEventBadgeClass(log.event_type)}`}>
                                                {log.event_type?.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td className={styles['action-cell']}>
                                            <div className={styles['action-cell-flex']}>
                                                <button
                                                    className={styles['log-view-btn']}
                                                    onClick={() => handleViewDetails(log)}
                                                    title="View Full Details"
                                                >
                                                    <MdVisibility size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className={styles['mobile-card-view']}>
                    {loading ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <Loader text="Fetching activity logs..." />
                        </div>
                    ) : logs.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <NoData title="No Logs Found" />
                        </div>
                    ) : (
                        logs.map((log, index) => (
                            <div key={log.id} className={styles['log-card']}>
                                <div className={styles['card-header']}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                                        <strong style={{ minWidth: '30px' }}>#{(currentPage - 1) * 10 + index + 1}</strong>
                                        <span className={`${styles['event-type-badge']} ${getEventBadgeClass(log.event_type)}`}>
                                            {log.event_type?.replace(/_/g, ' ')}
                                        </span>
                                    </div>
                                    <button
                                        className={styles['log-view-btn']}
                                        onClick={() => handleViewDetails(log)}
                                    >
                                        <MdVisibility size={18} />
                                    </button>
                                </div>
                                <div className={styles['card-body']}>
                                    <div className={styles['card-row']}>
                                        <span className={styles['card-label']}>Associate</span>
                                        <span className={styles['card-value']}>{log.Employee?.firstName} {log.Employee?.lastName}</span>
                                    </div>
                                    <div className={styles['card-row']}>
                                        <span className={styles['card-label']}>Date</span>
                                        <span className={styles['card-value']}>{formatDate(log.date)} {new Date(log.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    <div className={styles['card-row']} style={{ flexDirection: 'column', gap: '4px', borderTop: '1px solid #f1f5f9', paddingTop: '8px', marginTop: '4px' }}>
                                        <span className={styles['card-label']} style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action Done</span>
                                        <span className={styles['card-value']} style={{ fontSize: '0.9rem', color: '#166534', fontWeight: '500' }}>
                                            {getReadableDetails(log.details)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => setCurrentPage(page)}
                    totalRecords={totalRecords}
                    currentCount={logs.length}
                    entityName="History Logs"
                />
            </div>

            {/* Log Details Drawer */}
            <Drawer
                isOpen={showDetailsDrawer}
                onClose={() => setShowDetailsDrawer(false)}
                title="Activity Details"
            >
                <div className={styles['drawer-detail-content']}>
                    <div className={styles['detail-section']}>
                        <div className={styles['detail-section-title']}>Identification</div>
                        <div className={styles['data-list']}>
                            <div className={styles['data-list-item']}>
                                <div className={styles['data-label-col']}>Associate</div>
                                <div className={styles['data-value-col']}>
                                    {selectedLog?.Employee?.firstName} {selectedLog?.Employee?.lastName}
                                </div>
                            </div>
                            <div className={styles['data-list-item']}>
                                <div className={styles['data-label-col']}>Event Type</div>
                                <div className={styles['data-value-col']}>
                                    <span className={`${styles['event-type-badge']} ${getEventBadgeClass(selectedLog?.event_type)}`}>
                                        {selectedLog?.event_type?.replace(/_/g, ' ')}
                                    </span>
                                </div>
                            </div>
                            <div className={styles['data-list-item']}>
                                <div className={styles['data-label-col']}>Full Timestamp</div>
                                <div className={styles['data-value-col']}>
                                    {formatDate(selectedLog?.date)} {new Date(selectedLog?.date).toLocaleTimeString()}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles['detail-section']}>
                        <div className={styles['detail-section-title']}>Summary</div>
                        <div className={styles['readable-details']} style={{ margin: 0 }}>
                            <div className={styles['readable-header']}>Action Overview</div>
                            <div className={styles['readable-value']}>
                                {(() => {
                                    const details = selectedLog?.details;
                                    if (!details) return 'N/A';
                                    try {
                                        const obj = typeof details === 'string' ? JSON.parse(details) : details;
                                        return obj.action || 'No summary available';
                                    } catch (e) {
                                        return details.toString();
                                    }
                                })()}
                            </div>
                        </div>
                    </div>

                    {(() => {
                        try {
                            const details = selectedLog?.details;
                            if (!details) return null;
                            const obj = typeof details === 'string' ? JSON.parse(details) : details;
                            const data = obj.data;

                            if (data && typeof data === 'object') {
                                return (
                                    <div className={styles['detail-section']}>
                                        <div className={styles['detail-section-title']}>Detailed Activity Data</div>
                                        <div className={styles['data-list']}>
                                            {Object.keys(data)
                                                .filter(key => {
                                                    const lowKey = key.toLowerCase();
                                                    const EXCLUDED_FIELDS = ['id', 'uniqno', '1', '_changed', '_options', 'isnewrecord'];
                                                    const val = data[key];

                                                    // Skip excluded fields, ID fields, and empty values
                                                    return !EXCLUDED_FIELDS.includes(lowKey) &&
                                                        !lowKey.endsWith('id') &&
                                                        val !== null && val !== undefined && val !== '';
                                                })
                                                .map(key => (
                                                    <div key={key} className={styles['data-list-item']}>
                                                        <div className={styles['data-label-col']}>
                                                            {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                                                        </div>
                                                        <div className={styles['data-value-col']}>
                                                            {formatValue(data[key])}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        } catch (e) {
                            return null;
                        }
                    })()}
                </div>
            </Drawer>
        </div>
    );
};

export default HistoryLog;
